import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useCouponStore } from '../../services/coupons';
import { ICoupon, CouponUsageUser } from '../../types/coupons';
import { doGET } from '../../utils/HttpUtils';
import { downloadCsvFile } from '../../utils/export';

interface CouponUsageDialogProps {
    open: boolean;
    coupon: ICoupon | null;
    onClose: () => void;
}

interface CouponUsageUserDetail {
    userId: string;
    usageCount: number;
    totalDiscountAmount: number;
    contexts: string[];
    firstUsedAt?: string;
    lastUsedAt?: string;
    name: string;
    email: string;
    phone: string;
    corporateName: string;
}

const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatCurrency = (amount?: number) => {
    if (typeof amount !== 'number') return '-';
    return `₹${amount}`;
};

const USER_HYDRATION_BATCH_SIZE = 20;

export const hydrateCouponUsageUsers = async (
    usageUsers: CouponUsageUser[]
): Promise<CouponUsageUserDetail[]> => {
    let corporates: Array<{ _id?: string; companyName?: string; name?: string }> = [];
    try {
        const corporatesRes = await doGET('/corporates');
        corporates = Array.isArray(corporatesRes?.data?.data) ? corporatesRes.data.data : [];
    } catch (error) {
        corporates = [];
    }

    const corporateMap = new Map<string, string>(
        corporates.map((corporate) => [
            corporate._id || '',
            corporate.companyName || corporate.name || '',
        ])
    );
    const hydratedUsers: CouponUsageUserDetail[] = [];

    for (let index = 0; index < usageUsers.length; index += USER_HYDRATION_BATCH_SIZE) {
        const batch = usageUsers.slice(index, index + USER_HYDRATION_BATCH_SIZE);
        const hydratedBatch = await Promise.all(batch.map(async (usageUser) => {
            let userData: any = {};
            try {
                const userResponse = await doGET(`/users/${usageUser.userId}`);
                userData = userResponse?.data?.data?.user || userResponse?.data?.data || userResponse?.data || {};
            } catch (error) {
                userData = {};
            }

            return {
                userId: usageUser.userId,
                usageCount: usageUser.usageCount,
                totalDiscountAmount: usageUser.totalDiscountAmount,
                contexts: usageUser.contexts || [],
                firstUsedAt: usageUser.firstUsedAt,
                lastUsedAt: usageUser.lastUsedAt,
                name: userData?.name || '',
                email: userData?.email || '',
                phone: userData?.phone || '',
                corporateName: corporateMap.get(userData?.corporateId || '') || '',
            };
        }));
        hydratedUsers.push(...hydratedBatch);
    }

    return hydratedUsers;
};

export default function CouponUsageDialog({ open, coupon, onClose }: CouponUsageDialogProps) {
    const { fetchUsageByCode, fetchAllUsageByCode } = useCouponStore();
    const [loading, setLoading] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [summary, setSummary] = React.useState<{ couponCode: string; totalUses: number; totalUsers: number } | null>(null);
    const [users, setUsers] = React.useState<CouponUsageUserDetail[]>([]);

    const hydrateUsers = React.useCallback(hydrateCouponUsageUsers, []);

    const loadUsage = React.useCallback(async () => {
        if (!coupon?.code) return;

        setLoading(true);
        try {
            const response = await fetchUsageByCode(coupon.code, 1, 100);
            const payload = response?.data;

            if (!payload) {
                setSummary(null);
                setUsers([]);
                return;
            }

            setSummary({
                couponCode: payload.couponCode,
                totalUses: payload.totalUses,
                totalUsers: payload.totalUsers,
            });

            const hydratedUsers = await hydrateUsers(payload.users || []);
            setUsers(hydratedUsers);
        } finally {
            setLoading(false);
        }
    }, [coupon?.code, fetchUsageByCode, hydrateUsers]);

    React.useEffect(() => {
        if (open && coupon?.code) {
            loadUsage();
        } else if (!open) {
            setSummary(null);
            setUsers([]);
            setLoading(false);
            setExporting(false);
        }
    }, [open, coupon?.code, loadUsage]);

    const handleExport = async () => {
        if (!coupon?.code) return;

        setExporting(true);
        try {
            const response = await fetchAllUsageByCode(coupon.code);
            const payload = response?.data;
            const exportUsers = payload ? await hydrateUsers(payload.users || []) : [];

            if (!exportUsers.length) {
                return;
            }

            downloadCsvFile(
                `coupon-usage-${coupon.code}-${new Date().toISOString().slice(0, 10)}.csv`,
                ['Name', 'Email', 'Phone', 'Corporate Name', 'Usage Count', 'Coupon Discount'],
                exportUsers.map((user) => [
                    user.name,
                    user.email,
                    user.phone,
                    user.corporateName,
                    user.usageCount,
                    user.totalDiscountAmount,
                ])
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Coupon Usage Details</DialogTitle>
            <DialogContent dividers>
                {!coupon ? null : loading ? (
                    <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Loading coupon usage...
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{summary?.couponCode || coupon.code}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Uses: {summary?.totalUses ?? 0} | Total Users: {summary?.totalUsers ?? 0}
                            </Typography>
                        </Box>

                        <Divider />

                        {users.length > 0 ? (
                            <Stack spacing={2}>
                                {users.map((user, index) => (
                                    <Box
                                        key={`${user.userId}-${index}`}
                                        sx={{ border: '1px solid #eee', borderRadius: 2, p: 2 }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {user.name || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {user.email || 'No email'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {user.phone || 'No phone'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Typography variant="body2">
                                                    <strong>Corporate:</strong> {user.corporateName || '-'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    <strong>User ID:</strong> {user.userId}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={2}>
                                                <Typography variant="body2">
                                                    <strong>Discount:</strong> {formatCurrency(user.totalDiscountAmount)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    <strong>Usage Count:</strong> {user.usageCount}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Typography variant="body2">
                                                    <strong>First Used:</strong> {formatDateTime(user.firstUsedAt)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    <strong>Last Used:</strong> {formatDateTime(user.lastUsedAt)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    <strong>Contexts:</strong> {user.contexts.length ? user.contexts.join(', ') : '-'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography color="text.secondary">No coupon usage found.</Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleExport}
                    disabled={loading || exporting || !users.length}
                >
                    {exporting ? 'Exporting...' : 'Export Usage'}
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
